import React from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from "@mui/material";
import {
  Tune as TuneIcon,
  CheckCircle,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon
} from "@mui/icons-material";

const YearAccessEditor = ({
  hasPartialAccess,
  onPartialChange,
  allowed_yoa = [],
  availableYears = [],
  onToggleYear,
  onSelectAllYears,
  readOnly = false,
  disabled = false,
  fullAccessLabel = "Full Access",
  align = "right",
  alignNotPartial = "left"
}) => {
  const selectedYearsCount = Array.isArray(allowed_yoa)
    ? allowed_yoa.length
    : 0;

  if (readOnly) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: align === "right" ? "flex-end" : "flex-start"
        }}
      >
        {hasPartialAccess ? (
          <>
            <TuneIcon fontSize='small' color='info' />
            <Typography variant='body2' color='text.secondary'>
              {selectedYearsCount} years selected
            </Typography>
            <Chip
              label='Partial'
              size='small'
              variant='outlined'
              sx={{ height: 20, fontSize: "0.65rem", ml: 1 }}
            />
          </>
        ) : (
          <>
            <CheckCircle fontSize='small' color='success' />
            <Typography variant='body2' color='success.main' fontWeight={500}>
              {fullAccessLabel}
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: align === "right" ? "flex-end" : "flex-start",
        width: "100%"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          justifyContent:
            !hasPartialAccess && alignNotPartial === "right"
              ? "flex-end"
              : align === "right"
                ? "flex-end"
                : "flex-start",
          width: "100%"
        }}
      >
        <Tooltip title='Switch to specific tax computation years users can access'>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              borderRadius: 1,
              "&:hover": { bgcolor: "action.hover" }
            }}
            onClick={() => {
              if (!disabled) onPartialChange?.(!hasPartialAccess);
            }}
          >
            <Switch
              size='small'
              disabled={disabled}
              checked={!!hasPartialAccess}
              onChange={(e) => onPartialChange?.(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />

            <Typography variant='body2' fontWeight={600} color='text.primary'>
              {hasPartialAccess
                ? "Access to Selected Tax Years"
                : "Access to All Tax Years"}
            </Typography>
          </Box>
        </Tooltip>
        {hasPartialAccess ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TuneIcon fontSize='small' color='info' />
            <Typography variant='body2' color='text.secondary'>
              {selectedYearsCount} years
            </Typography>
            <Chip
              label='Partial'
              size='small'
              variant='outlined'
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          </Box>
        ) : null}
      </Box>
      {hasPartialAccess && availableYears.length > 0 && (
        <Accordion
          disableGutters
          sx={{
            bgcolor: "transparent",
            boxShadow: "none",
            "&:before": { display: "none" },
            m: 0,
            mt: 0.5,
            width: "100%",
            maxWidth: align === "right" ? "100%" : "100%"
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon fontSize='small' />}
            sx={{
              minHeight: 32,
              p: 0,
              "& .MuiAccordionSummary-content": { my: 0 }
            }}
          >
            <Typography variant='caption' fontWeight={600}>
              Modify Year Access
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 0,
              pt: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: align === "right" ? "flex-end" : "flex-start"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                maxHeight: 80,
                overflowY: "auto",
                p: 1,
                bgcolor: "grey.50",
                borderRadius: 1,
                width: "100%"
              }}
            >
              {availableYears.map((year) => {
                const isSelected = allowed_yoa.includes(year);
                return (
                  <Chip
                    key={year}
                    label={year}
                    size='small'
                    clickable={!disabled}
                    disabled={disabled}
                    onClick={() => onToggleYear?.(year)}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    sx={{ height: 24, fontSize: "0.7rem" }}
                  />
                );
              })}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent:
                  align === "right" ? "flex-end" : "space-between",
                mt: 1,
                gap: 1,
                width: "100%"
              }}
            >
              <Button
                size='small'
                disabled={disabled}
                onClick={() => onSelectAllYears?.(true)}
              >
                Select All
              </Button>
              <Button
                size='small'
                disabled={disabled}
                onClick={() => onSelectAllYears?.(false)}
              >
                Clear
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default YearAccessEditor;
